import React, { useEffect, useState, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Spinner } from "./ui-kit";

// Use a CDN-hosted worker so we don't depend on the static-file server's MIME type
// for `.mjs` (some hosts serve them as application/octet-stream which browsers reject).
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ fileUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [width, setWidth] = useState(800);
  const containerRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setWidth(containerRef.current.clientWidth - 32);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div className="flex flex-col h-full bg-zinc-100">
      <div className="flex items-center justify-center gap-2 px-3 py-2 border-b border-zinc-200 bg-white shrink-0">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pdf-prev-page"
          title="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-zinc-700 font-medium tabular-nums min-w-[60px] text-center">
          {numPages ? `${page} / ${numPages}` : "…"}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(numPages || p, p + 1))}
          disabled={!numPages || page >= numPages}
          className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pdf-next-page"
          title="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="w-px h-5 bg-zinc-200 mx-2" />
        <button
          onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)))}
          className="p-1.5 rounded hover:bg-zinc-100"
          data-testid="pdf-zoom-out"
          title="Zoom out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs text-zinc-500 tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
        <button
          onClick={() => setScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)))}
          className="p-1.5 rounded hover:bg-zinc-100"
          data-testid="pdf-zoom-in"
          title="Zoom in"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
      </div>
      <div ref={containerRef} className="flex-1 overflow-auto py-4 px-4 flex justify-center">
        <Document
          file={fileUrl}
          onLoadSuccess={(d) => { setNumPages(d.numPages); setPage(1); }}
          loading={<div className="py-20 flex justify-center"><Spinner /></div>}
          error={<p className="text-sm text-red-600 py-10">Could not load PDF.</p>}
        >
          <Page
            pageNumber={page}
            width={width * scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow-md rounded-md overflow-hidden bg-white"
          />
        </Document>
      </div>
    </div>
  );
};

export default PdfViewer;
